import { onMounted, ref } from 'vue'

import { parse } from '@/packages/link-parser'
import { setMeta } from '@/packages/meta'
import { delay } from '@/packages/utils/debug'
import {
    NambaMetricaPayload,
    sendAppDataWithRetry,
    sendAppEventWithRetry,
} from '@/packages/namba-metrics'

import { api } from '@/shared/api/rest'
import { useAppStore } from '@/shared/model'
import { env } from '@/shared/config'
import {
    BackendResponse,
    GetMainDataResponse,
    Status,
} from '@/shared/api/rest/types'
import { getServerNowMs } from '@/shared/api/create-request'

const isDev = env.NODE_ENV === 'development'
const MOCK_URL = env.MOCK_URL ?? ''
const FAKE_SOCKET_DELAY = 10000

export const useAppModel = () => {
    const {
        appStateReceived,
        setupUrl,
        setupUrlType,
        setupTimestamp,
        setupAutoRedirect,
        setupRemoveHeader,
        appStateExpired,
        setupAnalyticsData,
        state,
        setupStatus,
        setupServerTime,
    } = useAppStore()

    const href = isDev ? MOCK_URL : window.location.href
    const parseData = useGetMerchantResources(href)

    setupUrl(href)

    const socketTimeout = ref<ReturnType<typeof setTimeout>>()

    async function onExpired(
        {
            data,
            status,
        }: Omit<BackendResponse<GetMainDataResponse>, 'serverTime'>,
        isFirstCall = true
    ) {
        appStateExpired()
        clearTimeout(socketTimeout.value!)
        if (!isFirstCall) {
            await sendAppEventWithRetry({
                name: 'qrStatusChanged',
                backend: {
                    api: {
                        status,
                    },
                    data: {
                        status: 'expired',
                        qrId: data.qr_id,
                        amount: data.amount,
                        createdAt: data.created_at,
                        expiredAt: data.expires_at,
                    },
                },
            })
        }
    }

    async function onGetAppData(isFirstCall = true) {
        const {
            data: response,
            status,
            serverTime,
        } = await api.getMainData(href)

        setupServerTime(serverTime)

        const isStatusChanged = response.status !== state.status
        const isStateExpired = state.kind === 'expired'
        const isExpired =
            response.expires_at &&
            serverTime > new Date(response.expires_at).getTime()

        if (isStatusChanged && !isFirstCall) {
            sendAppEventWithRetry({
                name: 'qrStatusChanged',
                backend: {
                    api: {
                        status,
                    },
                    data: {
                        status: response.status,
                        qrId: response.qr_id,
                        amount: response.amount,
                        createdAt: response.created_at,
                        expiredAt: response.expires_at,
                    },
                },
            }).then()
        }

        if (!isStateExpired) {
            setStateData(response, parseData)
        }

        if (isExpired && response.status !== Status.COMPLETED) {
            onExpired({ data: response, status }, isFirstCall).then()
        }

        if (
            response.expires_at &&
            response.status !== Status.COMPLETED &&
            !isStateExpired
        ) {
            checkFutureTimestamp(new Date(response.expires_at).getTime(), () =>
                onExpired({ data: response, status }, isFirstCall)
            )
        }

        return {
            isProcessing:
                !isExpired && isStatusProcessing(response.status ?? null),
            response,
            status,
        }
    }
    let requestCount = 0

    async function init() {
        try {
            if (!parseData) {
                throw new Error('broken-link')
            }

            setMeta({
                faviconUrl: parseData?.faviconUrl,
            })

            const { isProcessing, response, status } = await onGetAppData()

            await sendAppDataWithRetry({
                backend: {
                    api: {
                        status,
                    },
                    data: {
                        status: response.status,
                        qrId: response.qr_id,
                        amount: response.amount,
                        createdAt: response.created_at,
                        expiredAt: response.expires_at,
                    },
                },
            })

            setupAnalyticsData(response)

            setMeta({
                title: response.merchant_title,
            })

            if (isProcessing) {
                await delay(FAKE_SOCKET_DELAY)
                await checkStatus(parseData)
            }
        } catch (e) {
            if (isDev) {
                console.log(e)
            }

            if (requestCount < 5 && (e as Error).message !== 'broken-link') {
                requestCount++

                setTimeout(async () => {
                    await init()
                }, 1000)
            } else {
                setMeta({
                    faviconUrl: '/favicon/default',
                })
                appStateReceived({
                    kind: 'api-fail',
                    merchant: null,
                })
            }

            await sendAppDataWithRetry({
                backend: {
                    api: {
                        status: (e as unknown as Error).message.replace(
                            'Error: ',
                            ''
                        ),
                    },
                },
            })
        }
    }

    async function checkStatus(
        parsedData: NonNullable<ReturnType<typeof useGetMerchantResources>>
    ) {
        try {
            clearTimeout(socketTimeout.value!)

            const { isProcessing } = await onGetAppData(false)

            if (isProcessing) {
                socketTimeout.value = setTimeout(
                    () => checkStatus(parsedData),
                    FAKE_SOCKET_DELAY
                )
                return
            }

            clearTimeout(socketTimeout.value!)
            return
        } catch (e) {
            socketTimeout.value = setTimeout(
                () => checkStatus(parsedData),
                FAKE_SOCKET_DELAY
            )
        }
    }

    function setStateData(
        response: GetMainDataResponse,
        parsed: ReturnType<typeof useGetMerchantResources>
    ) {
        if (!parsed) {
            throw new Error('broken-link')
        }
        appStateReceived({
            kind: statusToKind(response.status as Status),
            merchant: {
                slugId: parsed.slugId,
                name: response.merchant_title,
                logoUrl: response.merchant_logo ?? parsed.logoUrl,
                amount: (Number(response.amount) / 100).toString(),
                list: response.options?.elements ?? [],
                link: response.options?.redirectLink ?? '',
            },
        })

        setupStatus(response.status ?? null)
        setupTimestamp(
            response.expires_at ? new Date(response.expires_at).getTime() : null
        )
        setupUrlType(response.one_time ? 'dynamic' : 'static')
        setupAutoRedirect(Boolean(response.options?.autoRedirect))
        setupRemoveHeader(Boolean(response.options?.removeHeader))
    }

    function addEvents() {
        attachEventSendOnceWithRetry(
            window,
            'scroll',
            () => ({ name: 'scroll' }),
            { listenerOptions: { passive: true } }
        )
        attachEventSendOnceWithRetry(document, 'click', () => ({
            name: 'click',
        }))
        attachEventSendOnceWithRetry(
            document,
            'touchstart',
            () => ({ name: 'touch' }),
            { listenerOptions: { passive: true } }
        )
    }

    init().then()

    onMounted(addEvents)
}

function useGetMerchantResources(token: string) {
    const data = parse(token)

    if (!data?.qr_merchant_name) return null

    return {
        logoUrl: `${
            env.RESOURCES_DOMAIN
        }/${data?.qr_merchant_name.toLowerCase()}/theme`,
        faviconUrl: `${
            env.RESOURCES_DOMAIN
        }/${data?.qr_merchant_name.toLowerCase()}/favicon`,
        slugId: data?.qr_merchant_name.toString() ?? '',
    }
}

function statusToKind(status: Status) {
    switch (status) {
        case Status.COMPLETED:
            return 'resolved'
        case Status.CANCELLATION_ATTEMPTED:
        case Status.CANCELLATION_FAILED:
            return 'idle'
        case Status.EXPIRED:
            return 'invalid-state'
        case Status.FAILED:
        case Status.CANCELED:
            return 'rejected'
        default:
            return 'idle'
    }
}

function isStatusProcessing(status: Maybe<Status>) {
    return Boolean(
        [
            Status.PROCESSING,
            Status.CANCELLATION_ATTEMPTED,
            Status.CANCELLATION_FAILED,
            null,
        ].includes(status)
    )
}

let intervalId: ReturnType<typeof setInterval>

function checkFutureTimestamp(
    futureTimestamp: number,
    callback: () => void
): void {
    clearInterval(intervalId)
    function check() {
        const now = getServerNowMs()
        if (now >= futureTimestamp) {
            clearInterval(intervalId) // Останавливаем проверку
            callback() // Вызываем коллбек
        }
    }
    intervalId = setInterval(check, 1000) // Проверяем раз в секунду
}

function attachEventSendOnceWithRetry(
    target: EventTarget,
    eventName: string,
    makePayload: (e: Event) => NambaMetricaPayload,
    options?: {
        listenerOptions?: AddEventListenerOptions | boolean
    }
) {
    const listenerOptions = options?.listenerOptions

    let busy = false

    const handler = async (e: Event) => {
        if (busy) return // пока идут попытки — игнорим новые события
        busy = true

        try {
            const ok = await sendAppEventWithRetry(makePayload(e))

            if (ok) {
                target.removeEventListener(eventName, handler, listenerOptions)
            }
        } finally {
            busy = false
        }
    }

    target.addEventListener(eventName, handler, listenerOptions)
}
